/**
 * Persistencia de la sesión del procesador de FACTURAS EMITIDAS.
 * Mismo planteamiento que `useInvoiceSession` (recibidas): los PDFs se suben
 * a Storage y los metadatos se guardan en `invoice_sessions` con
 * environment = 'issued', para que el trabajo no se pierda al navegar por la app.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { IssuedFailedFile, IssuedProcessedFile } from "@/lib/issuedProcessing";
import type { SaveStatus } from "@/hooks/useInvoiceSession";

export type { SaveStatus };

const BUCKET = "invoice-files";
const ENVIRONMENT = "issued";
/** Subcarpeta propia para no mezclar con los archivos de recibidas. */
const folder = (userId: string) => `${userId}/emitidas`;

interface IssuedProcessedMeta extends Omit<IssuedProcessedFile, "file"> {
  storagePath: string;
  fileName: string;
  fileType: string;
}

interface IssuedFailedMeta extends Omit<IssuedFailedFile, "file"> {
  storagePath: string;
  fileName: string;
  fileType: string;
}

export interface IssuedSessionState {
  processed: IssuedProcessedFile[];
  failed: IssuedFailedFile[];
  companyId?: string | null;
}

interface SaveSessionOptions {
  immediate?: boolean;
}

const uploadFile = async (userId: string, fileId: string, file: File): Promise<string> => {
  const ext = file.name.split(".").pop() || "bin";
  const path = `${folder(userId)}/${fileId}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
  if (error) throw error;
  return path;
};

const downloadFile = async (path: string, fileName: string, fileType: string): Promise<File> => {
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error) throw error;
  return new File([data], fileName, { type: fileType });
};

const deleteFolder = async (userId: string) => {
  const { data } = await supabase.storage.from(BUCKET).list(folder(userId), { limit: 1000 });
  if (data && data.length > 0) {
    await supabase.storage.from(BUCKET).remove(data.map((f) => `${folder(userId)}/${f.name}`));
  }
};

const serialiseProcessed = (
  files: IssuedProcessedFile[],
  pathMap: Map<string, string>,
): IssuedProcessedMeta[] =>
  files.map(({ file, attachments: _att, ...rest }) => ({
    ...rest,
    storagePath: pathMap.get(rest.id) || "",
    fileName: file.name,
    fileType: file.type,
  }));

const serialiseFailed = (
  files: IssuedFailedFile[],
  pathMap: Map<string, string>,
): IssuedFailedMeta[] =>
  files.map(({ file, ...rest }) => ({
    ...rest,
    storagePath: pathMap.get(rest.id) || "",
    fileName: file.name,
    fileType: file.type,
  }));

export function useIssuedSession(userId: string | undefined) {
  const [loading, setLoading] = useState(true);
  const [initialState, setInitialState] = useState<IssuedSessionState | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const pathMapRef = useRef<Map<string, string>>(new Map());
  const uploadedIdsRef = useRef<Set<string>>(new Set());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestStateRef = useRef<IssuedSessionState | null>(null);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  // ─── Cargar sesión ─────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setInitialState(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const empty: IssuedSessionState = { processed: [], failed: [], companyId: null };
      try {
        const { data } = await supabase
          .from("invoice_sessions")
          .select("*")
          .eq("user_id", userId)
          .eq("environment", ENVIRONMENT)
          .maybeSingle();

        if (cancelled) return;
        if (!data) {
          setInitialState(empty);
          return;
        }

        const processedMeta = (data.processed_files as unknown as IssuedProcessedMeta[]) || [];
        const failedMeta = (data.failed_files as unknown as IssuedFailedMeta[]) || [];

        const results = await Promise.allSettled([
          ...processedMeta.map(async (m) => {
            if (!m.storagePath) return null;
            return { id: m.id, file: await downloadFile(m.storagePath, m.fileName, m.fileType), storagePath: m.storagePath };
          }),
          ...failedMeta.map(async (m) => {
            if (!m.storagePath) return null;
            return { id: m.id, file: await downloadFile(m.storagePath, m.fileName, m.fileType), storagePath: m.storagePath };
          }),
        ]);
        if (cancelled) return;

        const fileMap = new Map<string, File>();
        for (const r of results) {
          if (r.status === "fulfilled" && r.value) {
            fileMap.set(r.value.id, r.value.file);
            pathMapRef.current.set(r.value.id, r.value.storagePath);
            uploadedIdsRef.current.add(r.value.id);
          }
        }

        const processed = processedMeta
          .filter((m) => fileMap.has(m.id))
          .map((m) => {
            const { storagePath, fileName, fileType, ...rest } = m;
            return { ...rest, file: fileMap.get(m.id)! } as IssuedProcessedFile;
          });

        const failed = failedMeta
          .filter((m) => fileMap.has(m.id))
          .map((m) => {
            const { storagePath, fileName, fileType, ...rest } = m;
            return { ...rest, file: fileMap.get(m.id)! } as IssuedFailedFile;
          });

        setInitialState({
          processed,
          failed,
          companyId: (data as { applied_company_id?: string | null }).applied_company_id ?? null,
        });
      } catch (e) {
        console.error("Failed to load issued session:", e);
        if (!cancelled) setInitialState(empty);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  // ─── Guardar (debounce 1,5 s) ──────────────────────────
  const saveSession = useCallback(
    async (state: IssuedSessionState, options: SaveSessionOptions = {}) => {
      if (!userId) return;
      latestStateRef.current = state;
      setSaveStatus(options.immediate ? "saving" : "pending");
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }

      const persist = async (s: IssuedSessionState) => {
        setSaveStatus("saving");
        try {
          const allFiles = [
            ...s.processed.map((d) => ({ id: d.id, file: d.file })),
            ...s.failed.map((d) => ({ id: d.id, file: d.file })),
          ];
          const uploads = allFiles.filter((f) => !uploadedIdsRef.current.has(f.id));
          if (uploads.length > 0) {
            const POOL = 3;
            let i = 0;
            const worker = async () => {
              while (i < uploads.length) {
                const f = uploads[i++];
                try {
                  pathMapRef.current.set(f.id, await uploadFile(userId, f.id, f.file));
                  uploadedIdsRef.current.add(f.id);
                } catch (err) {
                  console.error("Failed to upload issued session file:", err);
                }
              }
            };
            await Promise.all(Array.from({ length: Math.min(POOL, uploads.length) }, () => worker()));
          }

          const { error } = await supabase.from("invoice_sessions").upsert(
            {
              user_id: userId,
              environment: ENVIRONMENT,
              processed_files: serialiseProcessed(s.processed, pathMapRef.current) as unknown as never,
              failed_files: serialiseFailed(s.failed, pathMapRef.current) as unknown as never,
              applied_company_id: s.companyId ?? null,
            },
            { onConflict: "user_id,environment" },
          );
          if (error) throw error;
          setLastSavedAt(Date.now());
          setSaveStatus("saved");
        } catch (e) {
          console.error("Failed to save issued session:", e);
          setSaveStatus("error");
        }
      };

      const enqueue = (s: IssuedSessionState) => {
        const queued = saveQueueRef.current
          .catch(() => undefined)
          .then(() => persist(s));
        saveQueueRef.current = queued;
        return queued;
      };

      if (options.immediate) {
        await enqueue(state);
        return;
      }

      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        const latest = latestStateRef.current;
        if (latest) void enqueue(latest);
      }, 1500);
    },
    [userId],
  );

  // ─── Finalizar / limpiar ───────────────────────────────
  const clearSession = useCallback(async () => {
    if (!userId) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    latestStateRef.current = null;

    // Una escritura que ya estuviera en curso podía terminar después del
    // borrado y volver a crear la sesión con los documentos anteriores.
    // Esperamos a que finalice antes de eliminar definitivamente la sesión.
    await saveQueueRef.current.catch(() => undefined);
    try {
      await Promise.allSettled([
        supabase.from("invoice_sessions").delete().eq("user_id", userId).eq("environment", ENVIRONMENT),
        deleteFolder(userId),
      ]);
    } catch (e) {
      console.error("Failed to clear issued session:", e);
    }
    pathMapRef.current.clear();
    uploadedIdsRef.current.clear();
    setInitialState({ processed: [], failed: [], companyId: null });
    setSaveStatus("idle");
    setLastSavedAt(null);
  }, [userId]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  return { loading, initialState, saveSession, clearSession, saveStatus, lastSavedAt };
}
